import React, { useCallback, useEffect, useState } from 'react';
import { PrinterOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { Col, Row, Statistic, Card, Table } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  AButton,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../../components';
import { useToast } from '../../../../hooks/toast';
import api from '../../../../services/api';
import { currency } from '../../../../shared/utils/functions';
import { getInitialFinalDateMonth } from '../../../../shared/utils/format-date';

interface IDREItem {
  description: string;
  value: number;
  type: 'RECEITA' | 'DESPESA' | 'RESULTADO';
  isTotal?: boolean;
}

const FinancialDRE: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const date = getInitialFinalDateMonth();

  const [loading, setLoading] = useState(false);
  const [date_initial, setDate_initial] = useState<string | null>(date.dateI);
  const [date_final, setDate_final] = useState<string | null>(date.dateF);
  const [reportData, setReportData] = useState<IDREItem[]>([]);
  const [result, setResult] = useState<number>(0);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.post('/financials/reports/dre', {
        date_initial,
        date_final,
      });
      setReportData(response.data.items);
      setResult(response.data.result);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao carregar o relatório DRE',
      });
    } finally {
      setLoading(false);
    }
  }, [date_initial, date_final, addToast]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const columns = [
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: IDREItem) => (
        <span style={{ fontWeight: record.isTotal ? 'bold' : 'normal' }}>
          {text}
        </span>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'value',
      key: 'value',
      align: 'right' as const,
      render: (value: number, record: IDREItem) => (
        <span
          style={{
            fontWeight: record.isTotal ? 'bold' : 'normal',
            color:
              record.type === 'DESPESA'
                ? '#cf1322'
                : record.type === 'RECEITA'
                  ? '#3f8600'
                  : value >= 0
                    ? '#3f8600'
                    : '#cf1322',
          }}
        >
          {currency(value)}
        </span>
      ),
    },
  ];

  return (
    <PageLayout>
      <PageIntro
        badge="Relatórios"
        icon={<PrinterOutlined />}
        title="DRE gerencial"
        description="Visualize receitas, despesas e resultado do exercício em um layout padronizado com as demais análises financeiras."
        actions={
          <AButton
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/financials')}
          >
            Voltar
          </AButton>
        }
      />
      <PagePanel>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card>
              <Statistic
                title="Resultado do Exercício"
                value={result}
                precision={2}
                valueStyle={{ color: result >= 0 ? '#3f8600' : '#cf1322' }}
                formatter={(val) => currency(val)}
              />
            </Card>
          </Col>
        </Row>
        <br />
        <Table
          dataSource={reportData}
          columns={columns}
          rowKey="description"
          pagination={false}
          loading={loading}
          bordered
          size="small"
        />
      </PagePanel>
    </PageLayout>
  );
};

export default FinancialDRE;
